import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ACCOUNT_TYPES, accountLabel, type CoverageRow, type ExpenseAccount } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage } from "../services/expensesService";
import { deleteAccount, fetchAccounts, fetchCoverage, saveAccount } from "../services/statementsService";
import { Chip, Dialog, ErrorBox, Field, Notice, Page, Spinner } from "../components/ui";
import { currentMonth, dateTime, monthLabel, shiftMonth } from "../lib/format";
import { ui } from "../theme";

// Cuentas bancarias y tarjetas + cobertura de estados de cuenta (#952): una
// fila por cuenta y una columna por mes, con los meses cargados, parciales y
// faltantes.

const NS = "expensesStatements";

const COVERAGE_TONES: Record<string, string> = {
  cargado: "bg-emerald-500 text-white",
  parcial: "bg-amber-400 text-gray-900",
  faltante: "bg-red-500/15 text-red-600",
  futuro: "bg-transparent text-gray-400",
};

type Form = { bank: string; alias: string; type: string; last4: string; currency: string; holder: string; active: boolean; notes: string };
const emptyForm: Form = { bank: "", alias: "", type: "cheques", last4: "", currency: "MXN", holder: "", active: true, notes: "" };
const toForm = (a: ExpenseAccount | null): Form =>
  a
    ? { bank: a.bank, alias: a.alias, type: a.type, last4: a.last4 || "", currency: a.currency || "MXN", holder: a.holder || "", active: a.active !== false, notes: a.notes || "" }
    : emptyForm;

export default function AccountsSection() {
  const { t, i18n } = useTranslation(["common", NS]);
  const tt = (k: string, o?: Record<string, unknown>) => t(`${NS}:${k}`, o);
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);

  const [accounts, setAccounts] = useState<ExpenseAccount[]>([]);
  const [coverage, setCoverage] = useState<{ from: string; to: string; rows: CoverageRow[]; unassigned: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ open: boolean; account: ExpenseAccount | null }>({ open: false, account: null });
  const [form, setForm] = useState<Form>(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const to = currentMonth();
      const [list, cov] = await Promise.all([fetchAccounts(), fetchCoverage({ from: shiftMonth(to, -11), to })]);
      setAccounts(list);
      setCoverage(cov);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const openDialog = (account: ExpenseAccount | null) => {
    setForm(toForm(account));
    setDialog({ open: true, account });
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await saveAccount(
        {
          bank: form.bank,
          alias: form.alias,
          type: form.type as ExpenseAccount["type"],
          last4: form.last4 || null,
          currency: form.currency.toUpperCase(),
          holder: form.holder || null,
          active: form.active,
          notes: form.notes || null,
        },
        dialog.account?.id,
      );
      setDialog({ open: false, account: null });
      await load();
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.save"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a: ExpenseAccount) => {
    if (!window.confirm(tt("accounts.confirmDelete", { name: accountLabel(a) }))) return;
    try {
      await deleteAccount(a.id);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.delete"));
    }
  };

  const forgetTemplate = async (a: ExpenseAccount) => {
    try {
      await saveAccount({ importTemplate: null }, a.id);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.save"));
    }
  };

  const set = (k: keyof Form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const months = coverage?.rows[0]?.months.map((m) => m.period) || [];

  return (
    <Page
      title={tt("menu.accounts")}
      subtitle={tt("accounts.subtitle")}
      actions={
        <button type="button" className={c.primary} onClick={() => openDialog(null)}>
          <i className="ri-add-line mr-1" />
          {tt("accounts.new")}
        </button>
      }
    >
      <ErrorBox message={error} />
      {loading && <Spinner />}

      <div className={`${c.card} overflow-x-auto p-0`}>
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className={`text-left text-xs ${c.muted}`}>
              <th className="px-4 py-2">{tt("accounts.bank")}</th>
              <th className="px-2 py-2">{tt("accounts.type")}</th>
              <th className="px-2 py-2">{tt("accounts.last4")}</th>
              <th className="px-2 py-2">{tt("accounts.currency")}</th>
              <th className="px-2 py-2">{tt("accounts.holder")}</th>
              <th className="px-2 py-2">{tt("accounts.template")}</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id} className={`border-t ${c.border} ${c.text} ${a.active === false ? "opacity-60" : ""}`} data-testid={`account-${a.id}`}>
                <td className="px-4 py-2">
                  <p className="font-medium">{a.bank}</p>
                  <p className={`text-xs ${c.muted}`}>
                    {a.alias}
                    {a.active === false && <Chip className="ml-2 bg-gray-400/15 text-gray-500">{tt("accounts.inactive")}</Chip>}
                  </p>
                </td>
                <td className="px-2 py-2 text-xs">{tt(`types.${a.type}`)}</td>
                <td className="px-2 py-2 tabular-nums">{a.last4 ? `·${a.last4}` : "—"}</td>
                <td className="px-2 py-2 text-xs">{a.currency}</td>
                <td className={`px-2 py-2 text-xs ${c.muted}`}>{a.holder || "—"}</td>
                <td className="px-2 py-2 text-xs">
                  {a.importTemplate ? (
                    <span title={dateTime(a.importTemplate.savedAt, i18n.language)}>
                      <Chip className="bg-teal-500/15 text-teal-600">{tt("accounts.templateSaved")}</Chip>
                      <button type="button" className="ml-2 text-teal-600" onClick={() => forgetTemplate(a)}>
                        {tt("accounts.clearTemplate")}
                      </button>
                    </span>
                  ) : (
                    <span className={c.muted}>{tt("accounts.noTemplate")}</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-right">
                  <button type="button" aria-label={`edit-${a.id}`} className="mr-3 text-teal-600" onClick={() => openDialog(a)}>
                    <i className="ri-edit-line" />
                  </button>
                  <button type="button" aria-label={`delete-${a.id}`} className="text-red-500" onClick={() => remove(a)}>
                    <i className="ri-delete-bin-line" />
                  </button>
                </td>
              </tr>
            ))}
            {!loading && accounts.length === 0 && (
              <tr>
                <td colSpan={7} className={`px-4 py-8 text-center text-sm ${c.muted}`}>
                  {tt("accounts.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className={`${c.card} space-y-3`}>
        <div>
          <h3 className={`text-sm font-semibold ${c.text}`}>{tt("accounts.coverage.title")}</h3>
          <p className={`text-xs ${c.muted}`}>{tt("accounts.coverage.subtitle")}</p>
        </div>
        {coverage && coverage.unassigned > 0 && <Notice tone="warn">{tt("accounts.coverage.unassigned", { count: coverage.unassigned })}</Notice>}
        {coverage && coverage.rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-xs" data-testid="coverage-grid">
              <thead>
                <tr className={c.muted}>
                  <th className="py-1 pr-3 text-left" />
                  {months.map((m) => (
                    <th key={m} className="px-0.5 py-1 text-center font-medium">
                      {monthLabel(m, i18n.language)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {coverage.rows.map((row) => (
                  <tr key={row.accountId} className={row.active ? "" : "opacity-60"}>
                    <td className={`whitespace-nowrap py-1 pr-3 ${c.text}`}>{row.label}</td>
                    {row.months.map((m) => (
                      <td key={m.period} className="px-0.5 py-1">
                        <div
                          className={`rounded px-1 py-1 text-center text-[11px] font-medium ${COVERAGE_TONES[m.status]}`}
                          title={m.status === "futuro" ? "" : tt("accounts.coverage.days", { covered: m.coveredDays, expected: m.expectedDays })}
                          data-testid={`cov-${row.accountId}-${m.period}`}
                        >
                          {tt(`accounts.coverage.${m.status}`)}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          !loading && <p className={`text-sm ${c.muted}`}>{tt("accounts.coverage.empty")}</p>
        )}
      </div>

      <Dialog
        open={dialog.open}
        title={dialog.account ? tt("accounts.editTitle") : tt("accounts.newTitle")}
        onClose={() => setDialog({ open: false, account: null })}
        footer={
          <>
            {busy && <Spinner />}
            <button type="button" className={c.secondary} onClick={() => setDialog({ open: false, account: null })}>
              {tt("common.cancel")}
            </button>
            <button type="button" className={c.primary} disabled={busy || !form.bank.trim() || !form.alias.trim()} onClick={save}>
              {tt("common.save")}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={tt("accounts.bank")}>
            <input aria-label="account-bank" className={c.input} value={form.bank} onChange={set("bank")} placeholder="BBVA, Banorte, American Express…" />
          </Field>
          <Field label={tt("accounts.alias")}>
            <input aria-label="account-alias" className={c.input} value={form.alias} onChange={set("alias")} placeholder={tt("accounts.aliasHint")} />
          </Field>
          <Field label={tt("accounts.type")}>
            <select aria-label="account-type" className={c.input} value={form.type} onChange={set("type")}>
              {ACCOUNT_TYPES.map((k) => (
                <option key={k} value={k}>
                  {tt(`types.${k}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={tt("accounts.last4")}>
            <input aria-label="account-last4" className={c.input} maxLength={4} inputMode="numeric" value={form.last4} onChange={set("last4")} placeholder="1234" />
          </Field>
          <Field label={tt("accounts.currency")}>
            <input aria-label="account-currency" className={c.input} maxLength={3} value={form.currency} onChange={set("currency")} />
          </Field>
          <Field label={tt("accounts.holder")}>
            <input aria-label="account-holder" className={c.input} value={form.holder} onChange={set("holder")} />
          </Field>
          <Field label={tt("accounts.notes")} className="sm:col-span-2">
            <input className={c.input} value={form.notes} onChange={set("notes")} />
          </Field>
          <label className={`flex items-center gap-2 text-sm ${c.muted}`}>
            <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />
            {tt("accounts.active")}
          </label>
        </div>
      </Dialog>
    </Page>
  );
}
